import {
    Component,
    Input,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    TrackByFunction,
    ChangeDetectionStrategy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { StrategicSectionData } from '../../../pages/strategic-plan/components/strategic-section-dialog/strategic-section-dialog.component';
import {
    StrategicPlan,
    StrategicGoal,
    StrategicPerspective,
    StrategicPillar,
    PlanType,
    StrategicValue,
    specialPlanTypes,
} from '@masar/common/models';

type ObjectivesViewMode = 'pillars' | 'perspectives' | 'both' | 'special';
type MapPerspective = StrategicPerspective & { solo?: boolean };

@Component({
    selector: 'app-strategic-plan-dashboard',
    templateUrl: './strategic-plan-dashboard.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class PlanDashboardComponent implements OnInit, OnDestroy, OnChanges {
    @Input() public strategicPlan?: StrategicPlan;
    @Input() public strategicSpecialPlan?: StrategicPlan;
    @Input() public planType: PlanType = 'normal';
    @Input() public showArrows?: boolean;
    @Input() public strategicSectionDialog?: {
        set: (data: StrategicSectionData) => void;
    };

    public pillars: StrategicPillar[] = [];
    public perspectives: MapPerspective[] = [];
    public standaloneGoals: StrategicGoal[] = [];
    public objectivesViewMode: ObjectivesViewMode = 'pillars';

    // Pre-computed section data
    public visionData: StrategicSectionData | null = null;
    public missionData: StrategicSectionData | null = null;
    public values: StrategicValue[] = [];
    public planName: string = '';
    public planPeriod: string = '';
    public isRtlLayout: boolean = false;
    public isExternalType: boolean = false;

    // Computed properties for template (avoid method calls)
    public pillarsWithComputedGoals: Array<
        StrategicPillar & {
            parentChildGoals: Array<StrategicGoal & { parentName: string }>;
            standaloneGoalsFromPillar: StrategicGoal[];
        }
    > = [];
    public standaloneParentChildGoals: Array<
        StrategicGoal & { parentName: string }
    > = [];
    public standaloneGoalsFromPillar: StrategicGoal[] = [];
    public hasStandaloneGoalsValue: boolean = false;

    // Memoized computed values for performance
    private pageTitleCache: string = '';
    private standaloneGoalsLabelCache: string = '';
    private unsubscribeAll = new Subject<void>();
    // TrackBy functions for performance optimization
    public readonly trackByPerspectiveId: TrackByFunction<MapPerspective> = (
        _index: number,
        perspective: MapPerspective
    ): string => perspective?.id || `perspective_${_index}`;
    public readonly trackByPillarId: TrackByFunction<StrategicPillar> = (
        _index: number,
        pillar: StrategicPillar
    ): string => pillar?.id || `pillar_${_index}`;
    public readonly trackByGoalId: TrackByFunction<StrategicGoal> = (
        _index: number,
        goal: StrategicGoal
    ): string => goal?.id || `goal_${_index}`;
    public readonly trackByValue: TrackByFunction<StrategicValue> = (
        _index: number,
        value: StrategicValue
    ): string => value?.id || value?.name || `value_${_index}`;

    public ngOnInit(): void {
        this.initializeComponent();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['strategicPlan'] ||
            changes['strategicSpecialPlan'] ||
            changes['planType']
        ) {
            this.initializeComponent();
        }
    }

    public ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    public isSingleNamedPillarGoal(pillar: StrategicPillar): boolean {
        const goals = pillar?.goals || [];
        const name = (pillar.name || '').trim();
        return (
            goals.length === 1 &&
            !!name &&
            name === (goals[0].name || '').trim()
        );
    }

    // Memoized computed properties for performance
    public get pageTitle(): string {
        return this.pageTitleCache;
    }

    public get standaloneGoalsLabel(): string {
        return this.standaloneGoalsLabelCache;
    }

    private checkRtlLayout(): boolean {
        // Check if the current language direction is RTL (Arabic)
        return (
            document.documentElement.dir === 'rtl' ||
            document.documentElement.lang === 'ar' ||
            localStorage.getItem('lang') === 'ar'
        );
    }

    private initializeComponent(): void {
        // Reset data first
        this.resetComponentData();

        // Set RTL layout state once
        this.isRtlLayout = this.checkRtlLayout();

        // Determine which plan to process
        const plan =
            this.planType === 'normal'
                ? this.strategicPlan
                : this.strategicSpecialPlan;

        // Process the plan data
        if (!plan) {
            this.setDefaultPlanData();
            return;
        }

        this.processPlanData(plan);
        this.computeMemoizedValues();
    }

    private resetComponentData(): void {
        this.pillars = [];
        this.perspectives = [];
        this.standaloneGoals = [];
        this.objectivesViewMode = 'pillars';
        this.visionData = null;
        this.missionData = null;
        this.values = [];
        this.planName = '';
        this.planPeriod = '';
        this.isRtlLayout = false;

        // Reset computed properties
        this.pillarsWithComputedGoals = [];
        this.standaloneParentChildGoals = [];
        this.standaloneGoalsFromPillar = [];
        this.hasStandaloneGoalsValue = false;

        // Reset memoized values
        this.pageTitleCache = '';
        this.standaloneGoalsLabelCache = '';
    }

    private setDefaultPlanData(): void {
        const currentYear = new Date().getFullYear();
        const isSpecialPlanType = ['special', 'internal', 'external'].includes(
            this.planType
        );

        this.pillars = [];
        this.perspectives = [];
        this.standaloneGoals = [];
        this.objectivesViewMode = 'pillars';
        this.visionData = null;
        this.missionData = null;
        this.values = [];
        this.planName = '';
        this.planPeriod = isSpecialPlanType
            ? `${currentYear} - ${currentYear + 5}`
            : '';
        this.showArrows = isSpecialPlanType;
    }

    private processPlanData(plan: StrategicPlan): void {
        // Determine if external type
        this.isExternalType = plan.type === specialPlanTypes.external;

        // Process vision and mission
        if (plan.vision || plan.mission) {
            this.visionData = {
                title: 'translate_vision',
                content: plan.vision || '',
                type: 'vision',
            };
            this.missionData = {
                title: 'translate_mission',
                content: plan.mission || '',
                type: 'mission',
            };
        }

        // Process based on plan type
        if (this.planType === 'normal') {
            this.processNormalPlan(plan);
        } else {
            this.processSpecialPlan(plan);
        }
    }

    private processNormalPlan(plan: StrategicPlan): void {
        const mapMode = plan.mapMode || 'pillars';
        this.objectivesViewMode = mapMode;
        if (mapMode === 'pillars') {
            this.pillars = (plan.pillars || [])
                .map(pillar => ({
                    id: pillar.id,
                    name: pillar.name || '',
                    nameAr: pillar.nameAr,
                    nameEn: pillar.nameEn,
                    iconClass: pillar.iconClass || 'fa-light fa-bullseye',
                    goals: (pillar.goals || []).map(goal => ({
                        ...goal,
                        id: goal.id || '',
                        name: goal.name || '',
                    })),
                }))
                .reverse();
            this.standaloneGoals = (plan.goals || []).map(goal => ({
                ...goal,
                id: goal.id || '',
                name: goal.name || '',
            }));
            this.perspectives = [];
        } else {
            this.pillars = [];
            this.standaloneGoals = [];
            this.perspectives = (plan.perspectives || []).map(p => ({
                ...p,
                solo: (p.pillars?.length ?? 0) === 1,
            }));
        }
        this.values = plan.values || [];
        this.planName = plan.name || '';
        this.planPeriod = plan.period || '';
        this.showArrows = false;
    }

    private processSpecialPlan(plan: StrategicPlan): void {
        this.objectivesViewMode = 'special';
        this.perspectives = [];
        this.pillars = (plan.pillars || [])
            .map(pillar => ({
                id: pillar.id,
                name: pillar.name || '',
                nameAr: pillar.nameAr,
                nameEn: pillar.nameEn,
                iconClass: pillar.iconClass || 'fa-light fa-bullseye',
                goals: (pillar.specialGoals || []).map(goal => ({
                    id: goal.id || '',
                    name: goal.name || '',
                    strategicGoal: goal.strategicGoal,
                    description: goal.description,
                    nameAr: goal.nameAr,
                    nameEn: goal.nameEn,
                    descriptionAr: goal.descriptionAr,
                    descriptionEn: goal.descriptionEn,
                })),
            }))
            .reverse();
        this.standaloneGoals = (plan.specialGoals || []).map(goal => ({
            id: goal.id || '',
            name: goal.name || '',
            strategicGoal: goal.strategicGoal,
            description: goal.description,
            nameAr: goal.nameAr,
            nameEn: goal.nameEn,
            descriptionAr: goal.descriptionAr,
            descriptionEn: goal.descriptionEn,
        }));
        this.values = (plan.specialValues || []) as StrategicValue[];
        this.planName = plan.name || '';
        this.planPeriod = `${plan.startYear} - ${plan.endYear}`;
        this.showArrows = true;
    }

    private computeMemoizedValues(): void {
        // Pre-compute frequently used values
        this.pageTitleCache = `${this.planName} ${this.planPeriod}`;
        this.standaloneGoalsLabelCache = this.isExternalType
            ? 'translate_external_goals'
            : this.planType === 'special'
              ? ''
              : 'translate_strategic_goals';

        // Compute pillars with their goals
        this.pillarsWithComputedGoals = this.pillars.map(pillar =>
            this.computePillarGoals(pillar)
        );

        // Compute standalone goals
        const isSpecialPlan = this.planType === 'special';

        this.standaloneParentChildGoals = this.standaloneGoals
            .filter(goal => isSpecialPlan && this.getParentGoalName(goal))
            .map(goal => ({
                ...goal,
                parentName: this.getParentGoalName(goal),
            }));

        this.standaloneGoalsFromPillar = this.standaloneGoals.filter(
            goal => !isSpecialPlan || !this.getParentGoalName(goal)
        );

        // Compute hasStandaloneGoals
        this.hasStandaloneGoalsValue = this.standaloneGoals.length > 0;
    }

    private computePillarGoals(pillar: StrategicPillar): StrategicPillar & {
        parentChildGoals: Array<StrategicGoal & { parentName: string }>;
        standaloneGoalsFromPillar: StrategicGoal[];
    } {
        const isSpecialPlan = this.planType === 'special';
        const goals = pillar.goals || [];

        const parentChildGoals = goals
            .filter(goal => isSpecialPlan && this.getParentGoalName(goal))
            .map(goal => ({
                ...goal,
                parentName: this.getParentGoalName(goal),
            }));

        const standaloneGoalsFromPillar = goals.filter(
            goal => !isSpecialPlan || !this.getParentGoalName(goal)
        );

        return {
            ...pillar,
            parentChildGoals,
            standaloneGoalsFromPillar,
        };
    }

    private getParentGoalName(goal: StrategicGoal): string {
        if ('strategicGoal' in goal && goal.strategicGoal) {
            return goal.strategicGoal.name || '';
        }
        return '';
    }
}
